import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { useSortable } from "@dnd-kit/react/sortable";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MotifIcon } from "@/components/MotifIcon";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import {
  CUSTOM_FIELD_OPTION_DESCRIPTION_MAX_LENGTH,
  CUSTOM_FIELD_OPTION_NAME_MAX_LENGTH,
} from "@/pages/applications/pages/customFields/constants";
import type {
  OptionDescriptionError,
  OptionNameError,
  OptionSection,
  SingleSelectOptionDraft,
} from "@/pages/applications/pages/customFields/utils/singleSelectOptions";
import {
  countOptionCharacters,
  parsePastedOptionNames,
} from "@/pages/applications/pages/customFields/utils/singleSelectOptions";

/**
 * How close to the limit the character counter appears when the field is not
 * focused. A counter on every row would be noise in a list that can hold 200 of
 * them; a counter only when it is about to matter is the signal.
 */
const NAME_COUNTER_VISIBLE_WITHIN = 20;

const styles = {
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  rootDragging: {
    opacity: 0.5,
  },
  main: {
    display: "flex",
    alignItems: "flex-start",
    gap: "4px",
  },
  dragHandle: {
    marginTop: "2px",
    color: theme.colors.staticFgNote,
    cursor: "grab",
    "&:active": {
      cursor: "grabbing",
    },
  },
  nameField: {
    flex: 1,
  },
  archivedNameField: {
    "& .MuiOutlinedInput-input": {
      color: theme.colors.staticFgNote,
    },
  },
  action: {
    marginTop: "2px",
    color: theme.colors.staticFgNote,
  },
  actionHasDescription: {
    color: theme.colors.staticFgPrimary,
  },
  counter: {
    color: theme.colors.staticFgNote,
    whiteSpace: "nowrap",
  },
  counterOverLimit: {
    color: theme.colors.staticFgNegative,
  },
  descriptionWrapper: {
    // Line the description up under the name field rather than the drag handle,
    // so an expanded row still reads as one item.
    paddingLeft: "38px",
    paddingRight: "76px",
  },
  descriptionField: {
    // Right-align the character count the way every other counter in the
    // custom field form sits, so an expanded row does not read as a different
    // kind of field.
    "& .MuiFormHelperText-root": {
      marginRight: 0,
      textAlign: "right",
    },
  },
  descriptionHelperError: {
    "& .MuiFormHelperText-root": {
      textAlign: "left",
    },
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

interface SingleSelectOptionRowProps {
  option: SingleSelectOptionDraft;
  /**
   * Which side of the archived divider this row is on. It is the row's archived
   * state, since there is no flag on the option itself, and it decides how the
   * row looks and what its last button does.
   */
  section: OptionSection;
  /**
   * Position in the editor's single sortable list, which counts the divider as
   * an entry of its own. `useSortable` needs it to work out what a drag passed
   * over.
   */
  index: number;
  nameError?: OptionNameError | null;
  descriptionError?: OptionDescriptionError | null;
  expanded: boolean;
  disabled?: boolean;
  onNameChange: (rowId: string, name: string) => void;
  /**
   * Called instead of `onNameChange` when the clipboard holds more than one
   * non-blank line, with the browser's own paste already prevented.
   */
  onNamesPaste?: (rowId: string, names: ReadonlyArray<string>) => void;
  onNameBlur?: (rowId: string) => void;
  onDescriptionChange: (rowId: string, description: string) => void;
  onExpandedChange: (rowId: string, expanded: boolean) => void;
  onSectionChange: (rowId: string, section: OptionSection) => void;
  /**
   * Called instead of `onSectionChange` for a row the server has never seen.
   * Required rather than optional. Every caller has rows that reach this
   * state, and the create modal opens on one.
   */
  onRemove: (rowId: string) => void;
  sx?: SxProps<typeof muiTheme>;
}

/**
 * One option in the editor: its name, an expandable description, and the
 * archive toggle.
 *
 * Presentational and fully controlled -- it reads no query and owns no option
 * state, so a story or a form can drive it the same way. The one piece of state
 * it does keep is whether its name input has focus, which only decides whether
 * the character counter shows.
 *
 * Split out from the editor because `useSortable` is a hook: it has to run once
 * per row, which means once per component instance rather than inside the
 * parent's `.map()`.
 */
export const SingleSelectOptionRow: React.FC<SingleSelectOptionRowProps> =
  function SingleSelectOptionRow({
    option,
    section,
    index,
    nameError,
    descriptionError,
    expanded,
    disabled = false,
    onNameChange,
    onNamesPaste,
    onNameBlur,
    onDescriptionChange,
    onExpandedChange,
    onSectionChange,
    onRemove,
    sx,
  }) {
    const { t } = useTranslation();
    const [isNameFocused, setIsNameFocused] = useState(false);
    const isArchived = section === "archived";

    // `handleRef` (not `ref`) is what makes only the handle draggable -- the row
    // itself stays a plain sortable target, so dragging never fights with
    // selecting text inside the inputs.
    const { ref, handleRef, isDragging } = useSortable({
      id: option.rowId,
      index,
    });

    const nameLength = countOptionCharacters(option.name);
    const showNameCounter =
      isNameFocused ||
      nameLength >
        CUSTOM_FIELD_OPTION_NAME_MAX_LENGTH - NAME_COUNTER_VISIBLE_WITHIN;

    const nameErrorMessage = useMemo(() => {
      if (!nameError) return undefined;
      const messages: Record<OptionNameError, string> = {
        empty: t("customField.singleSelect.error.nameRequired"),
        tooLong: t("customField.singleSelect.error.nameTooLong", {
          max: CUSTOM_FIELD_OPTION_NAME_MAX_LENGTH,
        }),
        multiline: t("customField.singleSelect.error.nameMultiline"),
        duplicate: t("customField.singleSelect.error.nameDuplicate"),
      };
      return messages[nameError];
    }, [nameError, t]);

    const descriptionErrorMessage = useMemo(() => {
      if (!descriptionError) return undefined;
      const messages: Record<OptionDescriptionError, string> = {
        tooLong: t("customField.singleSelect.error.descriptionTooLong", {
          max: CUSTOM_FIELD_OPTION_DESCRIPTION_MAX_LENGTH,
        }),
      };
      return messages[descriptionError];
    }, [descriptionError, t]);

    const handleNameChange = useHandler<
      React.ComponentProps<typeof TextField>["onChange"]
    >((event) => {
      onNameChange(option.rowId, event.target.value);
    });

    const handleNamePaste = useHandler<
      React.ComponentProps<typeof TextField>["onPaste"]
    >((event) => {
      if (!onNamesPaste) return;
      const names = parsePastedOptionNames(
        event.clipboardData.getData("text/plain"),
      );
      // One line is an ordinary paste into this field; let the browser do it,
      // including replacing whatever the user had selected.
      if (names.length < 2) return;
      event.preventDefault();
      onNamesPaste(option.rowId, names);
    });

    const handleNameFocus = useHandler(() => {
      setIsNameFocused(true);
    });

    const handleNameBlur = useHandler(() => {
      setIsNameFocused(false);
      onNameBlur?.(option.rowId);
    });

    const handleDescriptionChange = useHandler<
      React.ComponentProps<typeof TextField>["onChange"]
    >((event) => {
      onDescriptionChange(option.rowId, event.target.value);
    });

    const handleToggleExpanded = useHandler(() => {
      onExpandedChange(option.rowId, !expanded);
    });

    const handleToggleSection = useHandler(() => {
      onSectionChange(option.rowId, isArchived ? "active" : "archived");
    });

    const handleRemove = useHandler(() => {
      onRemove(option.rowId);
    });

    /**
     * A row the server has never seen is removed outright; one it has is
     * archived.
     *
     * Archiving is how an existing option leaves use: the server never
     * hard-deletes one, and its name stays occupied so a contact already set
     * to it can still be read back. A row added in this session has none of
     * that to preserve -- there is nothing stored, nothing pointing at it, and
     * no name being held -- so asking the server to keep it out of use would
     * be asking it to keep something it was never told about. The create modal
     * makes this plainest: every row there is new, so nothing in it can be
     * archived at all.
     */
    const isUnsaved = option.id === null;

    const dragHandleLabel = t("customField.singleSelect.option.dragHandle");
    const descriptionLabel = expanded
      ? t("customField.singleSelect.option.descriptionHide")
      : t("customField.singleSelect.option.descriptionShow");
    const lastButtonLabel = isUnsaved
      ? t("customField.singleSelect.option.remove")
      : isArchived
        ? t("customField.singleSelect.option.restore")
        : t("customField.singleSelect.option.archive");

    return (
      <Box
        ref={ref}
        data-test="option-row"
        sx={{
          ...styles.root,
          ...(isDragging && styles.rootDragging),
          ...sx,
        }}
      >
        <Box sx={styles.main}>
          {/*
           * The native `title` rather than the MUI Tooltip the other two
           * buttons use. Tooltip opens and closes a popper while the pointer is
           * on this button, and this button is the drag source: dnd-kit's
           * optimistic sorting abandons a reorder as soon as it sees the
           * sortable indices move under it, so a tooltip here stops drag from
           * working at all. Verified by removing it and watching the same drag
           * start to reorder.
           */}
          <IconButton
            ref={handleRef}
            size="small"
            disabled={disabled}
            sx={styles.dragHandle}
            title={dragHandleLabel}
            aria-label={dragHandleLabel}
          >
            <MotifIcon un-i-motif="drag" />
          </IconButton>
          <TextField
            size="small"
            fullWidth
            value={option.name}
            onChange={handleNameChange}
            onPaste={handleNamePaste}
            onFocus={handleNameFocus}
            onBlur={handleNameBlur}
            disabled={disabled}
            error={!!nameErrorMessage}
            helperText={nameErrorMessage}
            placeholder={t("customField.singleSelect.option.namePlaceholder")}
            sx={{
              ...styles.nameField,
              ...(isArchived && styles.archivedNameField),
            }}
            slotProps={{
              // Deliberately no `maxLength`: it counts UTF-16 code units, so it
              // would cut a name of emoji off well before the server's
              // code-point limit. The counter and the `tooLong` error measure
              // the same way the server does.
              input: {
                endAdornment: showNameCounter ? (
                  <Box
                    component="span"
                    sx={{
                      ...styles.counter,
                      ...(nameLength > CUSTOM_FIELD_OPTION_NAME_MAX_LENGTH &&
                        styles.counterOverLimit),
                    }}
                  >
                    <Typography variant="note" color="inherit">
                      {nameLength}/{CUSTOM_FIELD_OPTION_NAME_MAX_LENGTH}
                    </Typography>
                  </Box>
                ) : null,
              },
            }}
          />
          <Tooltip title={descriptionLabel} arrow>
            <Box component="span">
              <IconButton
                size="small"
                disabled={disabled}
                onClick={handleToggleExpanded}
                sx={{
                  ...styles.action,
                  // A filled-in description is worth seeing without opening
                  // every row, so the button carries that state.
                  ...(option.description.length > 0 &&
                    styles.actionHasDescription),
                }}
                aria-label={descriptionLabel}
                aria-expanded={expanded}
              >
                {/*
                 * `arrow_up` / `arrow_down` are chevrons. The `_2` variants are
                 * arrows with a stem, which in a list whose rows are dragged to
                 * reorder read as "move this row" rather than "open this row".
                 */}
                <MotifIcon un-i-motif={expanded ? "arrow_up" : "arrow_down"} />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title={lastButtonLabel} arrow>
            <Box component="span">
              <IconButton
                size="small"
                disabled={disabled}
                onClick={isUnsaved ? handleRemove : handleToggleSection}
                sx={styles.action}
                aria-label={lastButtonLabel}
              >
                {/*
                 * `bin` is what this repo already uses for deleting a record,
                 * and it is left in the row's own colour rather than the
                 * negative one the product feed list gives it: what goes here
                 * is a draft row that was never saved, so the warning weight
                 * would describe a loss bigger than the one taking place.
                 */}
                <MotifIcon
                  un-i-motif={
                    isUnsaved ? "bin" : isArchived ? "arrow_refresh" : "box"
                  }
                />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
        <Collapse in={expanded} unmountOnExit>
          <Box sx={styles.descriptionWrapper}>
            <TextField
              size="small"
              fullWidth
              multiline
              rows={2}
              value={option.description}
              onChange={handleDescriptionChange}
              disabled={disabled}
              error={!!descriptionErrorMessage}
              helperText={
                descriptionErrorMessage ??
                `${countOptionCharacters(option.description)}/${CUSTOM_FIELD_OPTION_DESCRIPTION_MAX_LENGTH}`
              }
              placeholder={t(
                "customField.singleSelect.option.descriptionPlaceholder",
              )}
              sx={{
                ...styles.descriptionField,
                ...(descriptionErrorMessage && styles.descriptionHelperError),
              }}
            />
          </Box>
        </Collapse>
      </Box>
    );
  };
